import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Label,
  Legend,
} from "recharts";

type ChartData = {
  year: string;
  month: string;
  completedDemos: number;
  pendingDemos: number;
};

type TotalCountProps = {
  totalCompletedDemos: number;
  totalPendingDemos: number;
};

const monthNames = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload?.length) {
    const completedValue = payload[0]?.value ?? 0;
    const pendingValue = payload[1]?.value ?? 0;
    const totalDemos = completedValue + pendingValue;

    const completedPercentage = totalDemos > 0
      ? ((completedValue / totalDemos) * 100).toFixed(0)
      : "0";

    return (
      <div className="bg-gray-800 text-white text-xs p-3 rounded-md shadow-lg">
        <p className="font-semibold mb-2 text-sm">{payload[0]?.payload?.month} {payload[0]?.payload?.year}</p>
        <div className="flex justify-between gap-4 mb-1">
          <span>Completed:</span>
          <span className="font-medium">{completedValue}</span>
        </div>
        <div className="flex justify-between gap-4 mb-2">
          <span>Follow Up:</span>
          <span className="font-medium">{pendingValue}</span>
        </div>
        <div className="mt-2 pt-2 border-t border-gray-600">
          <div className="w-full bg-gray-600 rounded-full h-2 mb-1">
            <div
              className="bg-green-500 h-2 rounded-full"
              style={{ width: `${completedPercentage}%` }}
            ></div>
          </div>
          <p className="text-right text-xs">{completedPercentage}% completed</p>
        </div>
      </div>
    );
  }
  return null;
};

interface RechartFlowProps {
  data: ChartData[];
  totalCount: TotalCountProps | null;
}

const RechartFlow = ({ data, totalCount }: RechartFlowProps) => {
  const [monthDemo, setMonthDemo] = useState<ChartData[]>([]);
  const year = data.length > 0 ? data[0].year : new Date().getFullYear().toString();

  useEffect(() => {
    const formattedData = data.map((item: any) => ({
      ...item,
      month: monthNames[item.month - 1], // Convert number to month name
    }));
    setMonthDemo(formattedData);
  }, [data]);

  return (
    <div className="w-full h-full flex flex-col md:flex-row gap-6">
      <div className="w-full md:w-[80%] h-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={monthDemo}
            margin={{ top: 10, right: 30, left: 10, bottom: 14 }}
          >
            <XAxis
              dataKey="month"
              tickLine={false}
              tick={{
                fontSize: 12,
                fontWeight: 500,
              }}
              height={40}
            >
              <Label
                value={`Performance Analysis - ${year}`}
                offset={-10}
                position="insideBottom"
                fontSize={14}
                fontWeight={600}
                style={{
                  textAnchor: "middle",
                }}
              />
            </XAxis>
            <YAxis
              tick={{
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <Label
                value="Demos"
                angle={-90}
                position="insideLeft"
                style={{ textAnchor: "middle" }}
                fontSize={14}
                fontWeight={600}
              />
            </YAxis>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              wrapperStyle={{
                paddingBottom: "20px"
              }}
            />
            <Bar
              dataKey="completedDemos"
              name="Completed Demos"
              fill="#10b981"
              radius={[4, 4, 0, 0]}
              barSize={30}
            />
            <Bar
              dataKey="pendingDemos"
              name="Follow Up Demos"
              fill="#f59e0b"
              radius={[4, 4, 0, 0]}
              barSize={30}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="flex md:flex-col justify-between md:w-64 gap-4">
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border-l-4 border-l-green-500">
          <div className="flex flex-col h-full justify-between">
            <h3 className="text-gray-500 dark:text-gray-400 text-sm">Completed Demos</h3>
            <div className="mt-2">
              <p className="text-3xl font-bold text-gray-800 dark:text-white">{totalCount?.totalCompletedDemos ?? 0}</p>
              <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div
                  className="bg-green-500 h-2.5 rounded-full"
                  style={(() => {
                    const total = (totalCount?.totalCompletedDemos ?? 0) + (totalCount?.totalPendingDemos ?? 0);
                    const pct = total > 0 ? (totalCount!.totalCompletedDemos / total) * 100 : 0;
                    return { width: `${pct}%` };
                  })()}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 border-l-4 border-l-amber-500">
          <div className="flex flex-col h-full justify-between">
            <h3 className="text-gray-500 dark:text-gray-400 text-sm">Follow Up Demos</h3>
            <div className="mt-2">
              <p className="text-3xl font-bold text-gray-800 dark:text-white">{totalCount?.totalPendingDemos ?? 0}</p>
              <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                <div
                  className="bg-amber-500 h-2.5 rounded-full"
                  style={(() => {
                    const total = (totalCount?.totalCompletedDemos ?? 0) + (totalCount?.totalPendingDemos ?? 0);
                    const pct = total > 0 ? (totalCount!.totalPendingDemos / total) * 100 : 0;
                    return { width: `${pct}%` };
                  })()}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RechartFlow;
